import { useState, useEffect, useCallback, useMemo } from 'react';
import { Expense, ExpenseWithDetails, CreateExpenseRequest, UpdateExpenseRequest, ExpenseSearchParams, ExpenseListResponse } from '../types';
import { expensesApi } from '../services/expenseApi';
import { useLoadingState } from '../../../../hooks/useLoadingState';
import { debounce } from '../../../../utils/validation';

export const useExpenses = () => {
  const [expenses, setExpenses] = useState<ExpenseWithDetails[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { isLoading: globalLoading, message: loadingMessage, startLoading, stopLoading } = useLoadingState(250);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  });

  const [searchParams, setSearchParams] = useState<ExpenseSearchParams>({
    search: '',
    page: 1,
    limit: 20,
    sorts: [{
      field: 'expenseDate',
      direction: 'desc'
    }]
  });

  const fetchExpenses = useCallback(async (params: ExpenseSearchParams = {}) => {
    setLoading(true);
    setError(null);
    startLoading('fetch-expenses', 'Loading expenses...');
    
    try {
      const response = await expensesApi.getAll(params);
      setExpenses(response.expenses);
      setPagination({
        page: params.page ?? searchParams.page ?? 1,
        limit: params.limit ?? searchParams.limit ?? 20,
        total: response.total,
        totalPages: Math.ceil(response.total / (params.limit ?? searchParams.limit ?? 20))
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch expenses');
    } finally {
      setLoading(false);
      stopLoading('fetch-expenses');
    }
  }, [startLoading, stopLoading, searchParams.page, searchParams.limit]);

  const debouncedFetchExpenses = useMemo(() => debounce(fetchExpenses, 300), [fetchExpenses]);

  useEffect(() => {
    fetchExpenses(searchParams);
  }, []);

  const updateSearchParams = useCallback((newParams: Partial<ExpenseSearchParams>) => {
    setSearchParams(prevParams => {
      const updatedParams = { ...prevParams, ...newParams };
      
      if (newParams.search !== undefined || newParams.fields !== undefined) {
        // Reset to first page when searching
        updatedParams.page = 1;
        debouncedFetchExpenses(updatedParams);
      } else if (newParams.sorts !== undefined) {
        // Reset to first page when sorting
        updatedParams.page = 1;
        fetchExpenses(updatedParams);
      } else if (newParams.page !== undefined || newParams.limit !== undefined) {
        // Handle page and limit changes immediately
        fetchExpenses(updatedParams);
      } else {
        fetchExpenses(updatedParams);
      }
      
      return updatedParams;
    });
  }, [debouncedFetchExpenses, fetchExpenses]);

  const createExpense = useCallback(async (expenseData: CreateExpenseRequest): Promise<Expense> => {
    startLoading('create-expense', 'Creating expense...');
    try {
      const response = await expensesApi.create(expenseData);
      await fetchExpenses(searchParams);
      return response;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create expense';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      stopLoading('create-expense');
    }
  }, [fetchExpenses, searchParams, startLoading, stopLoading]);

  const updateExpense = useCallback(async (expenseData: UpdateExpenseRequest): Promise<Expense> => {
    startLoading('update-expense', 'Updating expense...');
    try {
      const response = await expensesApi.update(expenseData.id, expenseData);
      await fetchExpenses(searchParams);
      return response;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update expense';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      stopLoading('update-expense');
    }
  }, [fetchExpenses, searchParams, startLoading, stopLoading]);

  const deleteExpense = useCallback(async (id: number): Promise<void> => {
    startLoading('delete-expense', 'Deleting expense...');
    try {
      await expensesApi.delete(id);
      await fetchExpenses(searchParams);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete expense';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      stopLoading('delete-expense');
    }
  }, [fetchExpenses, searchParams, startLoading, stopLoading]);

  const getExpenseById = useCallback(async (id: number): Promise<ExpenseWithDetails | null> => {
    startLoading('get-expense-by-id', 'Loading expense...');
    try {
      const response = await expensesApi.getById(id);
      return response;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch expense');
      return null;
    } finally {
      stopLoading('get-expense-by-id');
    }
  }, [startLoading, stopLoading]);

  return {
    expenses,
    loading,
    error,
    pagination,
    searchParams,
    updateSearchParams,
    createExpense,
    updateExpense,
    deleteExpense,
    getExpenseById,
    refetch: fetchExpenses,
    globalLoading,
    loadingMessage
  };
};


